import {
  QuoteContext,
  RewriteOption,
  useAIGenerator,
  useToast,
} from '@airship-platform/airship-patterns';
import {
  AIEditOptions,
  FlexBox,
  LoadingButton,
  SanitizedTrimmedHtml,
} from '@airship-platform/jam-web/components';
import { HTMLEditor } from '@airship-platform/jam-web/components-x';
import { LeftArrowIcon, PencilIcon } from '@airship-platform/jam-web/icons';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { useState } from 'react';

interface DescriptionEditableProps {
  description: string;
  title?: string;
  isEditable?: boolean;
  limit?: number;
  onUpdateDescription?: (newDescription: string) => void;
}

const DescriptionEditable: React.FC<DescriptionEditableProps> = ({
  description,
  title = 'Details',
  isEditable,
  limit = Infinity,
  onUpdateDescription,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [previousDescription, setPreviousDescription] = useState<string | null>(
    null
  );
  const { showToast } = useToast();
  const { isLoadingOption: isGenerating, generateQuote } = useAIGenerator();

  const toggleEditing = () => {
    setIsEditing((prev) => !prev);
  };

  const handleUndo = () => {
    if (previousDescription && onUpdateDescription) {
      onUpdateDescription(previousDescription);
      setPreviousDescription(null);
      showToast('Update reverted', 'info');
    }
  };

  const improveDescriptionHandler = async (rewriteOption: RewriteOption) => {
    const result = await generateQuote(
      description,
      rewriteOption,
      QuoteContext.PRODUCT_DESCRIPTION
    );
    if (result.length && onUpdateDescription) {
      setPreviousDescription(description);
      onUpdateDescription(result.map((r) => r.improvedQuote).join(' '));
      showToast('Your description has been improved!', 'success');
    } else {
      showToast(
        'We were unable to generate an improved description',
        'warning'
      );
    }
  };

  return (
    <FlexBox flexDirection="column" gap={2}>
      <FlexBox justifyContent="space-between" alignItems="center">
        <Typography variant="h4">{title}</Typography>
        {isEditable && (
          <IconButton color="softGold" onClick={toggleEditing}>
            <PencilIcon />
          </IconButton>
        )}
      </FlexBox>
      {!isEditing ? (
        <SanitizedTrimmedHtml html={description} />
      ) : (
        <>
          <HTMLEditor
            limit={limit}
            disabled={!!isGenerating}
            value={description}
            onChange={(newValue) =>
              onUpdateDescription && onUpdateDescription(newValue)
            }
          />
          <FlexBox gap={2} flexWrap="wrap" mt={2}>
            <LoadingButton
              color="softGold"
              startIcon={<LeftArrowIcon />}
              onClick={handleUndo}
              disabled={!!isGenerating || !previousDescription}
              loading={false}
            >
              Undo update
            </LoadingButton>
            <AIEditOptions
              onClick={improveDescriptionHandler}
              disabled={!!isGenerating}
              isLoadingOption={isGenerating}
            />
          </FlexBox>
        </>
      )}
    </FlexBox>
  );
};

export default DescriptionEditable;
